import { useCallback, useEffect, useMemo, useRef } from 'react'
import RecentIcons from '@/icons/RecentIcons'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ListRecentObjectWithCursor } from '@/modules/Drive/services/RecentObject'
import useAuthStore from '@/stores/useAuthStore'
import useDriveStore from '@/stores/useDriveStore'
import {
    Folder,
    File as DriveFile,
    RecentObjectResponse,
} from '@/modules/Drive/types'
import FileGrid from '../FileGrid'
import ViewTypeSwitch from '../ViewTypeSwitch'
import { useTranslation } from 'react-i18next'
import { categorizeObjectByDate } from '@/modules/Drive/utils'
import { useSearchParams } from 'react-router-dom'

const RecentFile = () => {
    const { t } = useTranslation()
    const employee = useAuthStore((state) => state.employee)
    const viewType = useDriveStore((state) => state.viewType)
    const setViewType = useDriveStore((state) => state.setViewType)
    const loadMoreRef = useRef<HTMLDivElement | null>(null)
    const [searchParams] = useSearchParams()

    const {
        data,
        isLoading,
        isError,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: [
            'recentList',
            employee?.employee_uuid,
            searchParams.get('search'),
        ],
        queryFn: ({ pageParam }) =>
            ListRecentObjectWithCursor(
                employee?.employee_uuid,
                pageParam,
                searchParams.get('search') ?? ''
            ),
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) =>
            lastPage?.result?.next_cursor || undefined,
        enabled: !!employee?.employee_uuid,
    })

    const recentItems = useMemo(
        () => data?.pages?.flatMap((p) => p?.result?.objects || []) || [],
        [data?.pages]
    )

    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage()
    }, [hasNextPage, isFetchingNextPage, fetchNextPage])

    useEffect(() => {
        if (!hasNextPage || isFetchingNextPage) return
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) handleLoadMore()
        })
        if (loadMoreRef.current) observer.observe(loadMoreRef.current)
        return () => {
            if (loadMoreRef.current) observer.unobserve(loadMoreRef.current)
        }
    }, [hasNextPage, isFetchingNextPage, handleLoadMore])

    // Categorize recent items by date
    const recentList = useMemo(() => {
        return recentItems.length > 0
            ? categorizeObjectByDate(recentItems)
            : undefined
    }, [recentItems])

    // Convert categorized items to folders and files for each time period
    const categorizedItems = useMemo(() => {
        if (!recentList) {
            return {
                today: { folders: [], files: [] },
                yesterday: { folders: [], files: [] },
                thisWeek: { folders: [], files: [] },
                earlier: { folders: [], files: [] },
            }
        }

        const convertToFoldersAndFiles = (items: RecentObjectResponse[]) => {
            const folders: Folder[] = []
            const files: DriveFile[] = []
            for (const item of items) {
                if (item.recent_object_key.endsWith('/')) {
                    folders.push({ Prefix: item.recent_object_key })
                } else {
                    files.push({
                        Key: item.recent_object_key,
                        Size: item.recent_object_size,
                        LastModified: item.recent_object_last_modified,
                    })
                }
            }
            return { folders, files }
        }

        return {
            today: convertToFoldersAndFiles(recentList.today),
            yesterday: convertToFoldersAndFiles(recentList.yesterday),
            thisWeek: convertToFoldersAndFiles(recentList.thisWeek),
            earlier: convertToFoldersAndFiles(recentList.earlier),
        }
    }, [recentList])

    const sections = useMemo(
        () => [
            { title: t('today'), items: categorizedItems.today },
            { title: t('yesterday'), items: categorizedItems.yesterday },
            { title: t('this-week'), items: categorizedItems.thisWeek },
            { title: t('earlier'), items: categorizedItems.earlier },
        ],
        [categorizedItems, t]
    )

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <RecentIcons className="w-5 h-5" />
                        <span>{t('recent-file-title')}</span>
                    </div>
                    <ViewTypeSwitch value={viewType} onChange={setViewType} />
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">{t('loading')}</div>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <RecentIcons className="w-5 h-5" />
                        <span>{t('recent-file-title')}</span>
                    </div>
                    <ViewTypeSwitch value={viewType} onChange={setViewType} />
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-red-500">
                        {t('error')}: {error?.message || t('unknown-error')}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <header className="flex items-center justify-between">
                <div className="font-semibold text-primary text-base flex items-center gap-3">
                    <RecentIcons className="w-5 h-5" />
                    <span>{t('recent-file-title')}</span>
                </div>
                <ViewTypeSwitch value={viewType} onChange={setViewType} />
            </header>

            {recentItems.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">{t('no-results')}</div>
                </div>
            ) : (
                <>
                    {sections.map(({ title, items }) => {
                        if (
                            items.folders.length === 0 &&
                            items.files.length === 0
                        ) {
                            return null
                        }
                        return (
                            <div key={title} className="mb-6">
                                <h2 className="text-sm text-gray-500 font-medium mb-3">
                                    {title}
                                </h2>
                                <FileGrid
                                    folders={items.folders}
                                    files={items.files}
                                />
                            </div>
                        )
                    })}
                    {hasNextPage && (
                        <div ref={loadMoreRef} style={{ height: 1 }} />
                    )}
                    {isFetchingNextPage && (
                        <div className="px-3 py-2 text-xs text-gray-500">
                            {t('loading')}...
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default RecentFile
